import React, { useState } from 'react'
import { Button, Select, Input, Switch, Tag, Tooltip, Popover, Space, message } from 'antd'
import {
  UserOutlined,
  TeamOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  CalendarOutlined,
  BankOutlined,
  SafetyOutlined,
  LockOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EyeOutlined,
  WalletOutlined,
  DollarOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CrownOutlined,
  StopOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import {
  MinimalContainer,
  UserHeader,
  UserAvatar,
  UserBasicInfo,
  ActionButtons,
  StatsCards,
  StatCard,
  InfoSection,
  InfoRow,
  InfoLabel,
  InfoValue,
  GroupTags,
  SecurityBadge,
  CompactMeta,
  CompactStatsBar,
  CompactStatItem,
  LevelBadge,
} from './MinimalStyles'
import moment from 'moment'
import ClassificationSelect from './ClassificationSelect'
import CountrySelect from '../CountrySelect'
import PhoneSelect from '../PhoneSelect'
import TextArea from 'antd/es/input/TextArea'
import API from 'utils/api'
import { useToken } from 'app/hooks'

const MinimalUserInfo = ({
  data,
  showForm,
  setShowForm,
  fold,
  setFold,
  // State values
  loginid,
  setID,
  selectLevel,
  setSelectLevel,
  group,
  setGroup,
  nickname,
  setNickname,
  fullname,
  setFullname,
  email,
  setEmail,
  phone,
  setPhone,
  phoneCode,
  setPhoneCode,
  countryCode,
  setCountryCode,
  referralCode,
  setReferralCode,
  bankName,
  setBankName,
  bankAccount,
  setBankAccount,
  bankOwner,
  setBankOwner,
  memo,
  setMemo,
  secver,
  setSecver,
  mobileBlock,
  setMobileBlock,
  wts,
  setWts,
  mts,
  setMts,
  app,
  setApp,
  ios,
  setIos,
  // Other props
  type1,
  detail,
  setDetail,
  balance,
  onSave,
  onPasswordReset,
  mutate,
}) => {
  const { data: token } = useToken()
  
  // Virtual account states
  const [vBank, setVBank] = useState('')
  const [vAccount, setVAccount] = useState('')
  const [vName, setVName] = useState('')
  
  const formatDate = (date) => {
    if (!date) return '-'
    return moment(date).format('YYYY.MM.DD HH:mm')
  }

  const formatRelativeTime = (date) => {
    if (!date) return ''
    return moment(date).fromNow()
  }

  const formatCurrency = (value) => {
    if (!value) return '0'
    return new Intl.NumberFormat('ko-KR').format(value)
  }

  if (fold) {
    return (
      <MinimalContainer style={{ padding: '16px 12px' }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: 10
        }}>
          <UserAvatar $level={data?.level} style={{ width: 42, height: 42, fontSize: 16 }}>
            {data?.fullname?.charAt(0) || data?.nickname?.charAt(0) || 'U'}
            <LevelBadge $level={data?.level}>
              {data?.level === -1 && <ClockCircleOutlined />}
              {data?.level === 0 && <StopOutlined />}
              {data?.level === 4 && <CrownOutlined />}
            </LevelBadge>
          </UserAvatar>
          <div style={{ 
            fontSize: 12, 
            fontWeight: 600,
            textAlign: 'center',
            color: '#1a1a1a',
            maxWidth: 90,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {data?.fullname || data?.nickname || '이름 없음'}
          </div>
          <Button
            type="text"
            size="small"
            onClick={() => setFold(false)}
            style={{ padding: '4px 12px', fontSize: 11 }}
          >
            <MenuUnfoldOutlined style={{ fontSize: 14 }} />
            <span style={{ marginLeft: 4 }}>펼치기</span>
          </Button>
        </div>
      </MinimalContainer>
    )
  }

  return (
    <MinimalContainer>
      {/* User Header */}
      <UserHeader>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <UserAvatar $level={data?.level}>
            {data?.fullname?.charAt(0) || data?.nickname?.charAt(0) || 'U'}
            <LevelBadge $level={data?.level}>
              {data?.level}
            </LevelBadge>
          </UserAvatar>
          <UserBasicInfo>
            <div className="name">
              {data?.fullname || data?.nickname || '이름 없음'}
              {data?.nickname && data?.fullname && (
                <span style={{ color: 'rgba(0,0,0,0.45)', fontWeight: 400, marginLeft: 6 }}>
                  @{data.nickname}
                </span>
              )}
            </div>
            <div className="meta">
              <span>#{data?.id}</span>
              <span className="divider" />
              <span>{data?.loginid}</span>
              <span className="divider" />
              <span>{formatRelativeTime(data?.lastlogin_at)}</span>
            </div>
          </UserBasicInfo>
        </div>
        <ActionButtons>
          <button onClick={() => setFold(true)}>
            <MenuFoldOutlined />
          </button>
        </ActionButtons>
      </UserHeader>

      {/* Compact Stats Bar */}
      <CompactStatsBar>
        <Tooltip title="예수금">
          <CompactStatItem>
            <WalletOutlined />
            <span>{formatCurrency(balance)}원</span>
          </CompactStatItem>
        </Tooltip>
        <Tooltip title="누적거래량">
          <CompactStatItem className={!detail ? 'clickable' : ''} onClick={() => !detail && setDetail(true)}>
            <DollarOutlined />
            {detail ? (
              <span>{formatCurrency(data?.total)} 계약</span>
            ) : (
              <span style={{ color: '#5c7cfa', textDecoration: 'underline' }}>보기</span>
            )}
          </CompactStatItem>
        </Tooltip>
        <Tooltip title="누적수수료">
          <CompactStatItem className={!detail ? 'clickable' : ''} onClick={() => !detail && setDetail(true)}>
            <DollarOutlined style={{ color: '#dc2626' }} />
            {detail ? (
              <span>-{formatCurrency(data?.feetotal)}원</span>
            ) : (
              <span style={{ color: '#5c7cfa', textDecoration: 'underline' }}>보기</span>
            )}
          </CompactStatItem>
        </Tooltip>
      </CompactStatsBar>

      {/* Account Info */}
      <InfoSection>
        <InfoRow>
          <InfoLabel>아이디</InfoLabel>
          <InfoValue style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {showForm ? (
              <Input
                value={loginid}
                onChange={(e) => setID(e.target.value)}
                size="small"
                style={{ flex: 1, marginRight: 8 }}
              />
            ) : (
              <span style={{ fontWeight: 500, color: '#5c7cfa' }}>{data?.loginid}</span>
            )}
            <div style={{ display: 'flex', gap: 4 }}>
              {showForm ? (
                <>
                  <Button
                    size="small"
                    onClick={() => {
                      // Reset all values to original
                      setID(data?.loginid)
                      setSelectLevel(data?.level)
                      setGroup(data?.group?.split('|')[0])
                      setNickname(data?.nickname)
                      setFullname(data?.fullname)
                      setEmail(data?.email)
                      setPhone(data?.phone)
                      setPhoneCode(data?.phone_code)
                      setCountryCode(data?.country_code)
                      setReferralCode(data?.referral_code)
                      setBankName(data?.bank_name)
                      setBankAccount(data?.bank_account)
                      setBankOwner(data?.bank_owner)
                      setMemo(data?.memo)
                      setWts(data?.wts)
                      setMts(data?.mts)
                      setApp(data?.app)
                      setIos(data?.ios)
                      setShowForm(false)
                    }}
                    style={{ fontSize: 11 }}
                  >
                    취소
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      onSave()
                      setShowForm(false)
                    }}
                    style={{ fontSize: 11 }}
                  >
                    저장
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  onClick={() => setShowForm(true)}
                  style={{ fontSize: 11 }}
                >
                  수정
                </Button>
              )}
            </div>
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>회원등급</InfoLabel>
          <InfoValue>
            <ClassificationSelect
              defaultValue={selectLevel}
              setSelectLevel={setSelectLevel}
              disabled={!showForm}
              style={{ width: '100%' }}
            />
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>그룹</InfoLabel>
          <InfoValue>
            {showForm ? (
              <Select
                value={group}
                onChange={setGroup}
                placeholder="그룹 선택"
                style={{ width: '100%' }}
                size="small"
                options={[
                  { value: '', label: '전체' },
                  ...(type1 || []),
                ]}
              />
            ) : (
              <GroupTags>
                {data?.group ? (
                  data.group.split(',').map((item, index) => (
                    <div key={index} className="group-tag">
                      <TeamOutlined />
                      {item.split('|')[1]}
                    </div>
                  ))
                ) : (
                  <span className="empty">그룹 없음</span>
                )}
              </GroupTags>
            )}
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>추천코드</InfoLabel>
          <InfoValue>
            {showForm ? (
              <Input
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                size="small"
              />
            ) : (
              <span>{data?.referral_code || <span className="empty">없음</span>}</span>
            )}
            {data?.referral_count > 0 && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                하위 {data.referral_count}명
              </Tag>
            )}
          </InfoValue>
        </InfoRow>
      </InfoSection>

      {/* Personal Info */}
      <InfoSection>
        <InfoRow>
          <InfoLabel>실명</InfoLabel>
          <InfoValue>
            {showForm ? (
              <Input
                value={fullname}
                onChange={(e) => setFullname(e.target.value)}
                size="small"
              />
            ) : (
              <span>{data?.fullname || <span className="empty">-</span>}</span>
            )}
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>닉네임</InfoLabel>
          <InfoValue>
            {showForm ? (
              <Input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                size="small"
              />
            ) : (
              <span>{data?.nickname || <span className="empty">-</span>}</span>
            )}
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>이메일</InfoLabel>
          <InfoValue>
            {showForm ? (
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="small"
                type="email"
              />
            ) : (
              <span>
                <MailOutlined style={{ color: 'rgba(0,0,0,0.35)', marginRight: 4 }} />
                {data?.email || <span className="empty">-</span>}
              </span>
            )}
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>연락처</InfoLabel>
          <InfoValue>
            {showForm ? (
              <div style={{ display: 'flex', gap: 4, width: '100%' }}>
                <PhoneSelect
                  phone={phone}
                  setPhone={setPhone}
                  phoneCode={phoneCode}
                  setPhoneCode={setPhoneCode}
                />
              </div>
            ) : (
              <span>
                <PhoneOutlined style={{ color: 'rgba(0,0,0,0.35)', marginRight: 4 }} />
                {data?.phone ? `${data.phone_code || ''}${data.phone}` : <span className="empty">-</span>}
              </span>
            )}
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>국가</InfoLabel>
          <InfoValue>
            <CountrySelect
              defaultValue={countryCode}
              setCountryCode={setCountryCode}
              disabled={!showForm}
              style={{ width: '100%' }}
            />
          </InfoValue>
        </InfoRow>
      </InfoSection>

      {/* Bank Info */}
      <InfoSection>
        <InfoRow>
          <InfoLabel>은행정보</InfoLabel>
          <InfoValue>
            {showForm ? (
              <div style={{ display: 'flex', gap: 4, width: '100%' }}>
                <Input
                  placeholder="은행명"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  size="small"
                  style={{ width: 80 }}
                />
                <Input
                  placeholder="계좌번호"
                  value={bankAccount}
                  onChange={(e) => setBankAccount(e.target.value)}
                  size="small"
                  style={{ flex: 1 }}
                />
                <Input
                  placeholder="예금주"
                  value={bankOwner}
                  onChange={(e) => setBankOwner(e.target.value)}
                  size="small"
                  style={{ width: 80 }}
                />
              </div>
            ) : (
              <span>
                <BankOutlined style={{ color: 'rgba(0,0,0,0.35)', marginRight: 4 }} />
                {data?.bank_name && data?.bank_account ? 
                  `${data.bank_name} ${data.bank_account} (${data.bank_owner})` : 
                  <span className="empty">등록된 계좌 없음</span>
                }
              </span>
            )}
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>입금가상계좌</InfoLabel>
          <InfoValue>
            {data?.vaccount ? (
              <span style={{ fontSize: 12 }}>
                {data.vaccount}
              </span>
            ) : (
              <Popover
                trigger="click"
                content={
                  <div style={{ width: 300 }}>
                    <div style={{ marginBottom: 10, fontSize: 12 }}>가상계좌를 발급하시겠습니까?</div>
                    <Space.Compact style={{ marginBottom: 10, width: '100%' }}>
                      <Input
                        placeholder="은행명"
                        value={vBank}
                        style={{ width: 80 }}
                        size="small"
                        onChange={e => setVBank(e.target.value)}
                      />
                      <Input
                        placeholder="가상계좌번호"
                        value={vAccount}
                        size="small"
                        onChange={e => setVAccount(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <Input
                        placeholder="입금자명"
                        style={{ width: 80 }}
                        value={vName}
                        size="small"
                        onChange={e => setVName(e.target.value)}
                      />
                    </Space.Compact>
                    <Button
                      style={{ width: '100%' }}
                      type="primary"
                      size="small"
                      onClick={() => {
                        if (vAccount?.trim() === '') {
                          message.error('가상계좌를 입력해주세요')
                          return
                        }
                        API.post('/admin/vaccount', token + '', {
                          id: data?.id,
                          vaccount: vBank?.trim() + '/' + vAccount?.trim() + '/' + vName?.trim(),
                        }).then(res => {
                          if (res?.result?.success) {
                            message.success('가상계좌가 발급되었습니다.')
                            if (mutate) mutate()
                            // Clear the form
                            setVBank('')
                            setVAccount('')
                            setVName('')
                          } else {
                            message.error('가상계좌 발급에 실패했습니다.')
                          }
                        })
                      }}
                    >
                      발급
                    </Button>
                  </div>
                }
              >
                <Button 
                  size="small" 
                  type="primary"
                  style={{ fontSize: 11 }}
                >
                  가상계좌 생성
                </Button>
              </Popover>
            )}
          </InfoValue>
        </InfoRow>
      </InfoSection>

      {/* Security & Settings */}
      <InfoSection>
        <InfoRow>
          <InfoLabel>보안프로그램</InfoLabel>
          <InfoValue>
            {data?.secver ? (
              <SecurityBadge active={true}>
                <SafetyOutlined />
                {data.secver}
              </SecurityBadge>
            ) : (
              <SecurityBadge active={false}>
                <CloseOutlined />
                미설치
              </SecurityBadge>
            )}
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>플랫폼 접속</InfoLabel>
          <InfoValue style={{ display: 'flex', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>WTS</span>
              <Switch
                checked={wts === 1 || wts === true}
                onChange={(checked) => setWts(checked ? 1 : 0)}
                disabled={!showForm}
                size="small"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>MTS</span>
              <Switch
                checked={mts === 1 || mts === true}
                onChange={(checked) => setMts(checked ? 1 : 0)}
                disabled={!showForm}
                size="small"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>APP</span>
              <Switch
                checked={app === 1 || app === true}
                onChange={(checked) => setApp(checked ? 1 : 0)}
                disabled={!showForm}
                size="small"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>iOS</span>
              <Switch
                checked={ios === 1 || ios === true}
                onChange={(checked) => setIos(checked ? 1 : 0)}
                disabled={!showForm}
                size="small"
              />
            </div>
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>모바일차단</InfoLabel>
          <InfoValue>
            <Switch
              checked={mobileBlock}
              onChange={(checked) => setMobileBlock(checked)}
              disabled={false}
              size="small"
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
            <span style={{ marginLeft: 8, fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
              {mobileBlock ? '차단됨' : '허용'}
            </span>
          </InfoValue>
        </InfoRow>

        <InfoRow>
          <InfoLabel>비밀번호</InfoLabel>
          <InfoValue>
            <Button 
              size="small" 
              danger
              style={{ fontSize: 11 }}
              onClick={() => {
                onPasswordReset()
              }}
            >
              비밀번호 초기화
            </Button>
          </InfoValue>
        </InfoRow>
      </InfoSection>

      {/* Memo */}
      <InfoSection>
        <TextArea
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          disabled={!showForm}
          rows={3}
          placeholder="관리자 메모를 입력하세요"
          style={{
            fontSize: 12,
            background: showForm ? 'white' : 'rgba(0,0,0,0.02)',
          }}
        />
      </InfoSection>


      {/* Compact Meta Info */}
      <CompactMeta>
        <div className="meta-item">
          <CalendarOutlined />
          <span>가입일:</span>
          <span className="value">{formatDate(data?.created_at)}</span>
        </div>
        <div className="meta-item">
          <CalendarOutlined />
          <span>최근접속:</span>
          <span className="value">{formatDate(data?.lastlogin_at)}</span>
        </div>
      </CompactMeta>
    </MinimalContainer>
  )
}

export default MinimalUserInfo